import { Box, Flex } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Icon } from '../../../../components/Icon'
import { LiveArtwork } from '../../../../services/Event/types'
import { SoldOverlay } from './SoldOverlay'

/**
 * Largura e espaçamento de cada miniatura, em px. São fixos de propósito: a conta que
 * centraliza a obra em destaque depende deles, e uma largura em % tornaria o cálculo
 * dependente do tamanho do container.
 */
/** Proporção das miniaturas medida no mockup: ~1.2:1, levemente deitadas. */
const ITEM_WIDTH = 200
const ITEM_HEIGHT = 165
const ITEM_GAP = 26

const FEATURED_SCALE = 1.15

/** Raio do brilho magenta que marca a obra em destaque. */
const FEATURED_GLOW_BLUR = 28

/** Ciclo do brilho que respira na obra em destaque, em ms. Lento de propósito: o telão
 *  fica horas ligado e um pulso rápido cansaria quem assiste. */
const FEATURED_GLOW_CYCLE_MS = 2500

/**
 * Folga vertical da faixa. Precisa acomodar tanto o crescimento da obra em destaque
 * (`ITEM_HEIGHT * (FEATURED_SCALE - 1) / 2`) quanto o alcance do brilho — senão o
 * `overflow: hidden` do container corta os dois numa linha reta.
 */
const TRACK_PADDING_Y = Math.ceil((ITEM_HEIGHT * (FEATURED_SCALE - 1)) / 2 + FEATURED_GLOW_BLUR) + 2

/**
 * Quantidade mínima de miniaturas renderizadas. A faixa é montada repetindo o acervo
 * até chegar nesse total, para que sempre haja obra à esquerda e à direita do centro
 * mesmo num leilão com poucas peças.
 */
const MIN_TILES = 21

/** Duração do deslize da faixa. A rede de segurança do reposicionamento se baseia nela. */
const TRACK_TRANSITION_MS = 600

interface IArtworkCarouselProps {
  artworks: LiveArtwork[]
  featuredIndex: number
  onSelect: (index: number) => void
  onNext: () => void
  onPrevious: () => void
}

interface IArrowProps {
  direction: 'left' | 'right'
  onClick: () => void
}

function CarouselArrow({ direction, onClick }: IArrowProps) {
  return (
    <Flex
      as="button"
      type="button"
      aria-label={direction === 'left' ? 'Obra anterior' : 'Próxima obra'}
      onClick={onClick}
      align="center"
      justify="center"
      w="44px"
      h="44px"
      flexShrink={0}
      borderRadius="full"
      bg="magenta.500"
      color="white"
      fontSize="20px"
      _hover={{ opacity: 0.85 }}
    >
      <Icon name={`lni lni-chevron-${direction}`} />
    </Flex>
  )
}

/**
 * Carrossel infinito das obras, com a obra em destaque sempre no centro.
 *
 * O acervo é repetido N vezes numa faixa só, e a posição corrente (`slot`) é um índice
 * absoluto dentro dessa faixa — nunca o índice da obra. Quando o `slot` sai da cópia do
 * meio, ele é reposicionado **com a transição desligada**: como a faixa se repete
 * exatamente a cada `total` miniaturas, o salto cai num ponto pixel a pixel idêntico e é
 * invisível. É isso que dá a volta sem o vazio nas pontas.
 *
 * O reposicionamento acontece no fim da animação (`transitionend`), que é o que o torna
 * imperceptível — mas esse evento **não é garantido**, então há uma rede de segurança por
 * tempo. Sem ela o slot cresce a cada rotação e a faixa sai de vista.
 */
export function ArtworkCarousel({ artworks, featuredIndex, onSelect, onNext, onPrevious }: IArtworkCarouselProps) {
  const total = artworks.length
  const copies = total > 1 ? Math.max(3, Math.ceil(MIN_TILES / total)) : 1
  const baseCopy = Math.floor(copies / 2)

  const slots = useMemo(
    () => Array.from({ length: copies * total }, (_, index) => artworks[index % total]),
    [artworks, copies, total]
  )

  const [slot, setSlot] = useState(() => baseCopy * total + featuredIndex)
  const [animate, setAnimate] = useState(true)

  // Uma obra a mais ou a menos muda o tamanho da faixa: recentraliza sem animar.
  useEffect(() => {
    setAnimate(false)
    setSlot(baseCopy * total)
  }, [total, baseCopy])

  // O destaque é do hook da tela (rotação de 10s, setas, clique). Aqui só andamos até o
  // slot equivalente mais próximo, para a faixa sempre girar pelo caminho mais curto.
  useEffect(() => {
    if (total < 2) return

    setSlot((current) => {
      const currentIndex = ((current % total) + total) % total
      if (currentIndex === featuredIndex) return current

      const forward = (((featuredIndex - currentIndex) % total) + total) % total
      const backward = forward - total

      return current + (forward <= -backward ? forward : backward)
    })
  }, [featuredIndex, total])

  // Traz o slot de volta para a cópia do meio. O módulo (em vez de um ±total) devolve
  // qualquer slot à faixa, inclusive um que já tenha se afastado várias cópias.
  const rebase = useCallback(() => {
    if (total < 2) return

    const low = baseCopy * total

    setSlot((current) => {
      if (current >= low && current < low + total) return current

      setAnimate(false)
      return low + ((((current - low) % total) + total) % total)
    })
  }, [baseCopy, total])

  // Reativa a transição só depois que o navegador pintou a posição reposicionada — com
  // um rAF só, React e browser podem agrupar as duas mudanças e o salto vira animação.
  useEffect(() => {
    if (animate) return undefined

    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setAnimate(true))
    })

    // rAF não roda em aba oculta. Sem este resgate a transição ficaria desligada para
    // sempre, e sem transição não há `transitionend` — que é quem reposiciona a faixa.
    const resgate = setTimeout(() => setAnimate(true), TRACK_TRANSITION_MS)

    return () => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
      clearTimeout(resgate)
    }
  }, [animate])

  // Rede de segurança do reposicionamento. O caminho normal é o `transitionend`, que
  // reposiciona no fim da animação e por isso é invisível — mas esse evento não é
  // garantido: não dispara em aba oculta nem quando a transição é interrompida. Sem esta
  // rede o slot cresce a cada rotação e a faixa inteira sai de vista.
  useEffect(() => {
    if (total < 2) return undefined

    const low = baseCopy * total
    if (slot >= low && slot < low + total) return undefined

    const timer = setTimeout(rebase, TRACK_TRANSITION_MS * 2)
    return () => clearTimeout(timer)
  }, [slot, baseCopy, total, rebase])

  const handleTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (event.propertyName !== 'transform') return
      rebase()
    },
    [rebase]
  )

  const handleSelect = useCallback(
    (slotIndex: number) => {
      setSlot(slotIndex)
      onSelect(((slotIndex % total) + total) % total)
    },
    [onSelect, total]
  )

  const offset = slot * (ITEM_WIDTH + ITEM_GAP) + ITEM_WIDTH / 2

  return (
    <Flex align="center" gap="1vw">
      <CarouselArrow direction="left" onClick={onPrevious} />

      <Box
        flex="1"
        overflow="hidden"
        position="relative"
        // As obras das pontas se dissolvem no fundo em vez de serem cortadas na régua.
        sx={{
          maskImage: 'linear-gradient(to right, transparent 0%, black 9%, black 91%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 9%, black 91%, transparent 100%)'
        }}
      >
        <Flex
          gap={`${ITEM_GAP}px`}
          w="max-content"
          py={`${TRACK_PADDING_Y}px`}
          // `left` é porcentagem da LARGURA DO CONTAINER (leva a origem da faixa ao meio) e o
          // translate desloca em px. Um `translateX(50%)` seria relativo à largura da própria
          // faixa — que aqui tem milhares de px — e jogaria tudo para fora da tela.
          position="relative"
          left="50%"
          transform={`translateX(-${offset}px)`}
          transition={animate ? `transform ${TRACK_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)` : 'none'}
          onTransitionEnd={handleTransitionEnd}
        >
          {slots.map((artwork, index) => {
            const isFeatured = index === slot

            return (
              <Box
                key={index}
                as="button"
                type="button"
                onClick={() => handleSelect(index)}
                w={`${ITEM_WIDTH}px`}
                h={`${ITEM_HEIGHT}px`}
                flexShrink={0}
                position="relative"
                // A obra em destaque cresce por cima das vizinhas: sem o z-index ela ficaria
                // sob a miniatura seguinte, que é pintada depois por ser irmã posterior.
                zIndex={isFeatured ? 1 : 0}
                borderRadius="lg"
                overflow="hidden"
                bg="#C6C6C6"
                // Sem borda quando não está em destaque: a borda transparente deixava o
                // fundo cinza da miniatura aparecer no anel de 1px, virando um contorno claro.
                border={isFeatured ? '2px solid' : 'none'}
                borderColor="magenta.500"
                // O contorno fino só desenha a borda; quem dá o destaque é o brilho, que
                // respira em ciclo lento. O box-shadow não é cortado pelo overflow do
                // próprio elemento.
                boxShadow={
                  isFeatured
                    ? `0 0 ${FEATURED_GLOW_BLUR}px rgba(250, 0, 105, 0.6), 0 0 12px rgba(250, 0, 105, 0.45)`
                    : 'none'
                }
                sx={
                  isFeatured
                    ? {
                        animation: `featuredGlowPulse ${FEATURED_GLOW_CYCLE_MS}ms ease-in-out infinite`,
                        '@keyframes featuredGlowPulse': {
                          '0%, 100%': {
                            boxShadow: `0 0 ${FEATURED_GLOW_BLUR * 0.6}px rgba(250, 0, 105, 0.45),
                              0 0 8px rgba(250, 0, 105, 0.35)`
                          },
                          '50%': {
                            boxShadow: `0 0 ${FEATURED_GLOW_BLUR * 1.35}px rgba(250, 0, 105, 0.85),
                              0 0 16px rgba(250, 0, 105, 0.6)`
                          }
                        }
                      }
                    : {}
                }
                transform={isFeatured ? `scale(${FEATURED_SCALE})` : 'scale(1)'}
                // `box-shadow` fica FORA da transição: ele é animado por keyframes quando a
                // obra está em destaque, e transição e animação na mesma propriedade brigam
                // no momento em que a miniatura perde o destaque.
                transition="transform 400ms ease, border-color 400ms ease"
                title={`${artwork.author_name} — ${artwork.name}`}
              >
                {artwork.art_image_url && (
                  <Box
                    as="img"
                    src={artwork.art_image_url}
                    alt={artwork.name}
                    w="100%"
                    h="100%"
                    // A obra preenche a miniatura, como no mockup — a fileira lê como uma
                    // galeria, não como caixas cinzas. O enquadramento sem corte fica por
                    // conta do quadro em destaque, que mostra a obra inteira.
                    objectFit="cover"
                    display="block"
                  />
                )}

                {artwork.sale_status === 'sold' && <SoldOverlay artwork={artwork} />}
              </Box>
            )
          })}
        </Flex>
      </Box>

      <CarouselArrow direction="right" onClick={onNext} />
    </Flex>
  )
}
